import { useTimeframeNavigation } from '@griply/hooks';
import {
  CreateTask,
  fallbackTimeframe,
  ID,
  InsightsTimeframes,
  navigableInsightsTimeframes,
  Task,
  TaskType,
} from '@griply/types';
import React, { useMemo, useState } from 'react';
import { useToday } from 'shared/contexts/today';

import { MetaContentTabs } from './components/meta-content-tabs';
import { MetaSettings } from './components/meta-settings';
import { MetaStatisticsTimeframe } from './components/meta-statistics-timeframe';
import { Statistics } from './components/statistics';
import { Upcoming } from './components/upcoming';
import * as Styled from './meta.style';
import { MetaViewOptions } from './types';
import { useData } from './use-data';

export type TaskDetailMetaProps = {
  task: Task | CreateTask;
  updateTask: (task: Partial<Task>) => void;
  updateRelationships: (relationshipIds: ID[]) => void;
  view: MetaViewOptions;
  updateView: (view: MetaViewOptions) => void;
  isCreate?: boolean;
  onDelete?: () => void;
  onArchive?: () => void;
};

export const Meta: React.FC<TaskDetailMetaProps> = ({
  task,
  updateTask,
  updateRelationships,
  view,
  updateView,
  isCreate,
  onDelete,
  onArchive,
}) => {
  const {
    timeframe: initialTimeframe,
    updateHabitTimeframe,
    weekStartsOn,
  } = useData();
  const today = useToday();

  const [timeframe, setTimeframe] = useState(
    navigableInsightsTimeframes.includes(initialTimeframe)
      ? initialTimeframe
      : fallbackTimeframe,
  );

  const { referenceDate, onPrev, onToday, onNext, isNextDisabled } =
    useTimeframeNavigation(timeframe, { today, weekStartsOn });

  const updateTimeframe = (newTimeframe: InsightsTimeframes) => {
    setTimeframe(newTimeframe);
    updateHabitTimeframe?.(newTimeframe);
  };

  const content = useMemo(() => {
    if (
      view === MetaViewOptions.Statistics &&
      'id' in task &&
      task.type === TaskType.Habit
    ) {
      return (
        <Statistics
          task={task}
          timeframe={timeframe}
          referenceDate={referenceDate}
        />
      );
    }

    if (
      view === MetaViewOptions.Upcoming &&
      'id' in task &&
      task.type !== TaskType.Task
    ) {
      return <Upcoming task={task} />;
    }

    return (
      <MetaSettings
        task={task}
        updateTask={updateTask}
        updateRelationships={updateRelationships}
        isCreate={isCreate}
        onDelete={onDelete}
        onArchive={onArchive}
      />
    );
  }, [
    view,
    task,
    updateTask,
    updateRelationships,
    isCreate,
    timeframe,
    referenceDate,
    onDelete,
    onArchive,
  ]);

  return (
    <Styled.Container>
      {'id' in task && task?.type !== TaskType.Task && (
        <Styled.Header>
          <MetaContentTabs
            onChange={updateView}
            selected={view}
            hideInsights={task.type !== TaskType.Habit}
          />
          {view === MetaViewOptions.Statistics && (
            <MetaStatisticsTimeframe
              timeframe={timeframe}
              onChange={updateTimeframe}
              onPrev={onPrev}
              onToday={onToday}
              onNext={onNext}
              isNextDisabled={isNextDisabled}
            />
          )}
        </Styled.Header>
      )}
      <Styled.Body>{content}</Styled.Body>
    </Styled.Container>
  );
};
